import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { connectorInvoke, isConnectorAvailable } from '../lib/connector';
import { useMaestroStore } from '../store/maestroStore';
import { isScanActive, subscribeScan } from '../services/libraryScanCoordinator';
import {
  getLocalLibraryAlbumsIpcPending,
  subscribeLocalLibraryAlbumsIpcPending,
} from '../services/localLibraryAlbumsIpcPending';

const HEARTBEAT_MS = 2_000;

function invokeInteractive(active: boolean): void {
  if (!isConnectorAvailable()) return;
  void connectorInvoke('local_library_set_interactive', { active }).catch((err) => {
    console.warn('[localLibraryInteractive] local_library_set_interactive failed:', err);
  });
}

/**
 * Tell the Connector to prioritize foreground local catalog reads over background
 * scan/tag-read writers. Heartbeat keeps the flag alive; clears on unmount/idle.
 */
export function useLocalLibraryInteractive(enabled: boolean): void {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    if (!enabled) {
      invokeInteractive(false);
      return undefined;
    }
    invokeInteractive(true);
    const id = window.setInterval(() => {
      if (enabledRef.current) invokeInteractive(true);
    }, HEARTBEAT_MS);
    return () => {
      window.clearInterval(id);
      invokeInteractive(false);
    };
  }, [enabled]);
}

/**
 * App-level gate: heartbeat during local playback, library routes, active scan,
 * and while getAlbums IPC is in flight (first-scan grid / wizard).
 */
export function useLocalLibraryInteractiveGate(): void {
  const location = useLocation();
  const currentTrack = useMaestroStore((s) => s.currentTrack);
  const playingLocal = currentTrack?.source === 'local_library' || !!currentTrack?.file_path;
  const onLocalLibrary = location.pathname.includes('/local-library');
  const [scanActive, setScanActive] = useState(() => isScanActive());
  const [albumsPending, setAlbumsPending] = useState(() => getLocalLibraryAlbumsIpcPending());

  useEffect(() => subscribeScan(() => setScanActive(isScanActive())), []);
  useEffect(
    () => subscribeLocalLibraryAlbumsIpcPending(() => setAlbumsPending(getLocalLibraryAlbumsIpcPending())),
    [],
  );

  const enabled = playingLocal || onLocalLibrary || scanActive || albumsPending > 0;
  useLocalLibraryInteractive(enabled);
}
